import type { ColumnMapping, ColumnRole, ParsedRow } from "@/lib/ingest";

/**
 * Auto-generate gate — decides whether a dropped file draws its map immediately,
 * skipping the manual "Make map" click. The confirm step exists to let a user
 * catch a mis-detected column mapping *before* it costs a geocode, so we only
 * skip it when the auto-detection is trustworthy enough that drawing is safe.
 */

const ADDRESS_ROLES: ColumnRole[] = [
  "address_full",
  "street",
  "city",
  "state",
  "zip",
  "country",
];

/**
 * True when the auto-detected mapping is confident enough to draw the map with
 * no manual confirm:
 *  - Coordinate mode renders locally with NO network — always auto-draw once a
 *    lat/lng pair is detected and at least one row is plottable.
 *  - Address mode fires a billed geocode, so auto-draw ONLY when every detected
 *    location column is HIGH confidence. A shaky guess (or a file with no
 *    address columns at all) keeps the confirm step, so we never geocode the
 *    wrong columns or spend quota on a dead-end file.
 *
 * Column indices can legitimately be 0, so presence is tested with `!= null`,
 * never truthiness.
 */
export function shouldAutoMakeMap(mapping: ColumnMapping, rows: ParsedRow[]): boolean {
  if (!rows.some((row) => row.status === "ready")) return false;

  if (mapping.mode === "coordinate") {
    return mapping.roles.lat != null && mapping.roles.lng != null;
  }

  const detected = ADDRESS_ROLES.filter((role) => mapping.roles[role] != null);
  if (detected.length === 0) return false;
  return detected.every((role) => mapping.confidence[role] === "high");
}
